/** Echo Index clearance thresholds (display scale 0–100). */

export const ECHO_THRESHOLD_CLEARED = 78;
export const ECHO_THRESHOLD_REWRITE_MIN = 67;

/** Development mandate fit threshold (commission mandate match %). */
export const MANDATE_THRESHOLD_PASS = 70;

export type ScriptClearanceStatus = 'cleared' | 'rewrite' | 'high_risk' | 'queued' | 'active';

/** overall_score from API is 0–1000 scale (tenths × 10). */
export function echoScoreDisplay(overallScore: number | undefined | null): number | null {
  if (overallScore == null || Number.isNaN(overallScore)) return null;
  return overallScore / 10;
}

export function clearanceFromEchoScore(
  overallScore: number | undefined | null,
): ScriptClearanceStatus {
  const display = echoScoreDisplay(overallScore);
  if (display == null) return 'queued';
  if (display >= ECHO_THRESHOLD_CLEARED) return 'cleared';
  if (display >= ECHO_THRESHOLD_REWRITE_MIN) return 'rewrite';
  return 'high_risk';
}

export function clearanceLabel(status: ScriptClearanceStatus): string {
  switch (status) {
    case 'cleared':
      return 'CLEARED';
    case 'rewrite':
      return 'REWRITE';
    case 'high_risk':
      return 'HIGH RISK';
    case 'queued':
      return 'QUEUED';
    case 'active':
      return 'ACTIVE';
    default:
      return 'ACTIVE';
  }
}

export function passesMandate(mandateMatchPct: number | undefined | null): boolean {
  return mandateMatchPct != null && !Number.isNaN(mandateMatchPct) && mandateMatchPct >= MANDATE_THRESHOLD_PASS;
}

/** Script passes board review when engagement is cleared AND mandate fit meets threshold. */
export function passesDualClearance(
  overallScore: number | undefined | null,
  mandateMatchPct: number | undefined | null,
): boolean {
  return clearanceFromEchoScore(overallScore) === 'cleared' && passesMandate(mandateMatchPct);
}

export function dualClearanceLabel(
  overallScore: number | undefined | null,
  mandateMatchPct: number | undefined | null,
): string {
  const echo = clearanceFromEchoScore(overallScore);
  const mandate = passesMandate(mandateMatchPct);
  if (echo !== 'cleared') {
    return clearanceLabel(echo);
  }
  if (mandateMatchPct == null) return 'CLEARED';
  return mandate ? 'CLEARED' : 'MANDATE GAP';
}

export function auditStatusFromWorkflow(
  steps: { analyse?: string; upload?: string; generate_scorecard?: string },
  overallScore?: number | null,
): string {
  if (steps.analyse === 'in_progress') return 'AUDIT RUN';
  if (steps.analyse === 'complete') {
    const c = clearanceFromEchoScore(overallScore ?? undefined);
    if (c === 'cleared') return 'CLEARED';
    if (c === 'rewrite') return 'REWRITE';
    if (c === 'high_risk') return 'HIGH RISK';
    return 'AUDITED';
  }
  if (steps.upload === 'complete') return 'INGESTED';
  return 'QUEUED';
}
